import { useEffect, useState } from "react";
import { BetSlipFragment } from "../../graphql/core-types";
import './bet-list.css';
import { ReactComponent as PlayersLoungeLogo } from "../../assets/logo/players-lounge-logo.svg";
import { usePresence } from "../../helpers/use-presence";
import { markOverlayBusy, useOverlayBusy } from "../../helpers/overlay-activity";
import { AnimatedHeight } from "../animated-height/animated-height";
import { PendingBetRow } from "./pending-bet-row";
import { BetCardBody } from "./bet-card-body";
import { Side } from "../../helpers/overlay-settings";

const MAX_OLDER_PENDING_ROWS = 4
const TIME_AGO_REFRESH_MS = 30000
// keep in sync with the leave transitions in bet-list.css
const CARD_EXIT_MS = 350
const ROW_EXIT_MS = 400
const CONTAINER_TRANSITION_MS = 750

type BetListProps = {
  side: Side
  betSlip: BetSlipFragment | null
  olderPendingBetSlips?: BetSlipFragment[]
}

const getBetSlipKey = (betSlip: BetSlipFragment) => betSlip.id
// null is the empty state, it crossfades in and out like any other card
const getCardKey = (betSlip: BetSlipFragment | null) => betSlip?.id ?? 'empty'

// one persistent card: switching bets crossfades the contents instead of sliding the whole card out and back in.
// with no pending bet it shows an empty state rather than leaving the screen
const EmptyCardBody = () => (
  <div className="bet_list__empty">
    <span className="bet_list__empty_heading pl-fontMContrast">No active bets</span>
    <span className="bet_list__empty_subheading pl-fontS">New bets will show up here</span>
  </div>
)

export const BetList = ({ side, betSlip, olderPendingBetSlips = [] }: BetListProps) => {
  const [hasMounted, setHasMounted] = useState(false)
  const [now, setNow] = useState(() => Date.now())

  const cardEntries = usePresence([betSlip], getCardKey, CARD_EXIT_MS)
  const visibleOlderBetSlips = olderPendingBetSlips.slice(0, MAX_OLDER_PENDING_ROWS)
  const rowEntries = usePresence(visibleOlderBetSlips, getBetSlipKey, ROW_EXIT_MS)

  const isVisible = hasMounted
  const isPendingTabVisible = isVisible && visibleOlderBetSlips.length > 0
  const hiddenOlderCount = olderPendingBetSlips.length - visibleOlderBetSlips.length
  const hasIncomingCard = cardEntries.some((entry) => entry.state !== 'leaving')

  // bets cycling in or out of the card or the pending tab count as activity for the scheduled refresh
  useOverlayBusy('bet-list', [...cardEntries, ...rowEntries].some((entry) => entry.state !== 'present'))

  // the panel sliding in or out
  useEffect(() => {
    markOverlayBusy('bet-list-panel', CONTAINER_TRANSITION_MS)
  }, [isVisible, isPendingTabVisible])

  // start off screen so the first bet slides in
  useEffect(() => {
    const timeout = setTimeout(() => setHasMounted(true), 50)
    return () => clearTimeout(timeout)
  }, [])

  // keep the "placed x ago" labels fresh while the card is on screen
  useEffect(() => {
    if (!isVisible) return
    setNow(Date.now())
    const interval = setInterval(() => setNow(Date.now()), TIME_AGO_REFRESH_MS)
    return () => clearInterval(interval)
  }, [isVisible])

  return (
    <div className={`bet_list__container bet_list__container--${side} ${isVisible ? 'bet_list__container--active' : 'bet_list__container--inactive'}`}>
      <div className="bet_list__wrapper">
        <PlayersLoungeLogo className="bet_list__logo" />
        <AnimatedHeight className="bet_list__body_height">
          <div className="bet_list__body_stack">
            {cardEntries.map((entry) => (
              <div
                key={entry.key}
                // an outgoing card only floats over the incoming one, otherwise it holds the height while the panel slides away
                className={`bet_list__layer bet_list__layer--${entry.state} ${entry.state === 'leaving' && hasIncomingCard ? 'bet_list__layer--overlay' : ''}`}>
                {entry.item ? <BetCardBody betSlip={entry.item} /> : <EmptyCardBody />}
              </div>
            ))}
          </div>
        </AnimatedHeight>
      </div>

      <AnimatedHeight>
        {rowEntries.length > 0 && (
          <div className={`bet_list__pending_tab ${isPendingTabVisible ? 'bet_list__pending_tab--visible' : 'bet_list__pending_tab--hidden'}`}>
            <span className="bet_list__stat_label pl-fontXS">Other pending bets</span>
            <ul className="bet_list__pending_rows">
              {rowEntries.map((entry) => (
                <li key={entry.key} className={`bet_list__pending_item bet_list__pending_item--${entry.state}`}>
                  <PendingBetRow betSlip={entry.item} now={now} />
                </li>
              ))}
            </ul>
            {hiddenOlderCount > 0 && (
              <span className="bet_list__pending_more pl-fontXS">+{hiddenOlderCount} more</span>
            )}
          </div>
        )}
      </AnimatedHeight>
    </div>
  )
}
